/**
 * Location service for handling city and province parsing, validation, and URL building.
 */

import {
  getCityBySlug,
  getCitiesByProvince,
  formatCityDisplay,
  type City
} from './data/cities';

import { PROVINCE_OPTIONS } from '@/features/puppy-search/services/collapsibleSearchHeader';

export type LocationType = 'city' | 'province' | null;

export interface ResolvedLocation {
  type: LocationType;
  slug: string;
  label: string;
  /** For city, the province info. For province, null. */
  province: { slug: string; label: string } | null;
  /** For city, the city data. For province, null. */
  city: City | null;
}

/**
 * Parse a location slug and determine if it's a city or province.
 * City slugs are like "toronto-on", province slugs are like "ontario".
 *
 * @returns ResolvedLocation with type, slug, and label, or null if invalid
 */
export function parseLocationSlug(slug: string): ResolvedLocation | null {
  if (!slug) return null;

  const normalized = slug.toLowerCase().trim();

  // Check if it's a city first (city slugs include province abbr, e.g., "toronto-on")
  const city = getCityBySlug(normalized);
  if (city) {
    const province = PROVINCE_OPTIONS.find((p) => p.slug === city.provinceSlug);
    return {
      type: 'city',
      slug: city.slug,
      label: formatCityDisplay(city),
      province: province
        ? { slug: province.slug, label: province.label }
        : { slug: city.provinceSlug, label: city.provinceSlug },
      city
    };
  }

  // Check if it's a province
  const province = PROVINCE_OPTIONS.find((p) => p.slug === normalized);
  if (province) {
    return {
      type: 'province',
      slug: province.slug,
      label: province.label,
      province: { slug: province.slug, label: province.label },
      city: null
    };
  }

  return null;
}

/**
 * Check if a location slug is valid (either city or province).
 */
export function isValidLocation(slug: string): boolean {
  return parseLocationSlug(slug) !== null;
}

/**
 * Check if a location slug is a city.
 */
export function isCity(slug: string): boolean {
  return parseLocationSlug(slug)?.type === 'city';
}

/**
 * Check if a location slug is a province.
 */
export function isProvince(slug: string): boolean {
  return parseLocationSlug(slug)?.type === 'province';
}

/**
 * Get the province slug for a location (works for both cities and provinces).
 */
export function getProvinceSlugForLocation(
  locationSlug: string
): string | null {
  const resolved = parseLocationSlug(locationSlug);
  return resolved?.province?.slug ?? null;
}

/**
 * Get cities for a location's province (works for both cities and provinces).
 */
export function getCitiesForLocation(locationSlug: string): City[] {
  const resolved = parseLocationSlug(locationSlug);
  if (!resolved?.province) return [];
  return getCitiesByProvince(resolved.province.slug);
}

/**
 * Build breadcrumb items for a city page.
 */
export function buildCityBreadcrumbs(
  breedName: string,
  breedSlug: string,
  city: City
): Array<{ label: string; href?: string }> {
  const province = PROVINCE_OPTIONS.find((p) => p.slug === city.provinceSlug);
  const provinceName = province?.label ?? city.provinceSlug;

  return [
    { label: 'Home', href: '/' },
    { label: 'Puppies', href: '/puppies' },
    { label: breedName, href: `/puppies/${breedSlug}` },
    { label: provinceName, href: `/puppies/${breedSlug}/${city.provinceSlug}` },
    { label: city.name }
  ];
}

/**
 * Build breadcrumb items for a province page.
 */
export function buildProvinceBreadcrumbs(
  breedName: string,
  breedSlug: string,
  provinceName: string
): Array<{ label: string; href?: string }> {
  return [
    { label: 'Home', href: '/' },
    { label: 'Puppies', href: '/puppies' },
    { label: breedName, href: `/puppies/${breedSlug}` },
    { label: provinceName }
  ];
}

/**
 * Build URL for breed + location page.
 */
export function buildBreedLocationUrl(
  breedSlug: string,
  locationSlug: string
): string {
  return `/puppies/${breedSlug}/${locationSlug}`;
}

/**
 * Get nearby cities (same province, different from current city).
 */
export function getNearbyCities(
  currentCitySlug: string,
  limit: number = 10
): City[] {
  const currentCity = getCityBySlug(currentCitySlug);
  if (!currentCity) return [];

  return getCitiesByProvince(currentCity.provinceSlug)
    .filter((city) => city.slug !== currentCitySlug)
    .slice(0, limit);
}

/**
 * Get all province options with their cities for dropdowns.
 */
export function getProvinceWithCitiesOptions(): Array<{
  province: { slug: string; label: string };
  cities: City[];
}> {
  return PROVINCE_OPTIONS.map((province) => ({
    province: { slug: province.slug, label: province.label },
    cities: getCitiesByProvince(province.slug)
  })).filter((item) => item.cities.length > 0);
}

/**
 * Re-export commonly used items from cities.ts for convenience.
 */
export {
  CITIES,
  getCityBySlug,
  getCitiesByProvince,
  formatCityDisplay,
  getStaticCitySlugs,
  getCitiesByTier,
  isValidCity
} from './data/cities';

export type { City, CityTier } from './data/cities';
